import { useState } from "react";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import { documentBuilderService } from "../../services/documentBuilderService";
import { Page } from "../../services/documentBuilderService";
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  Typography,
  TextField,
  Button,
  Divider,
} from "@mui/material";

interface PageAIChatBoxProps {
  page: Page;
  onReplace: (updatedPage: Page) => void;
  onClose: () => void;
}

export function PageAIChatBox({
  page,
  onReplace,
  onClose,
}: PageAIChatBoxProps) {
  const [prompt, setPrompt] = useState("");
  const [aiText, setAiText] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    if (!prompt) return;

    setLoading(true);

    try {
      const updatedPage = await documentBuilderService.enhanceSelectedPage(
  page,
prompt
);

      // API returns full Page object
      setAiText(updatedPage.body);
    } catch (error) {
      console.error("AI Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      elevation={10}
      sx={{
        position: "fixed",
        right: 20,
        top: 80,
        width: 680,
        maxWidth: "95vw",
        height: "80vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: (theme) => theme.palette.background.paper,
        zIndex: 1300,
      }}
    >
      <CardContent sx={{ flex: 1, overflow: "auto" }}>
        {/* ───── HEADER ───── */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <AutoFixHighIcon />
            <Typography fontWeight={600}>Rewrite Full Page</Typography>
          </Stack>

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* ───── PROMPT INPUT ───── */}
        <Typography variant="caption" color="text.secondary">
          What would you like to change?
        </Typography>

        <TextField
          fullWidth
          multiline
          minRows={3}
          placeholder="Example: Improve tone, make it more professional, expand content..."
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          sx={{
            mt: 1,
            "& .MuiInputBase-input": {
              color: (theme) => theme.palette.text.secondary,
            },
          }}
        />

        {/* ───── AI RESULT ───── */}
        {aiText && (
          <Box mt={3}>
            <Typography variant="caption" color="text.secondary">
              AI Generated Page
            </Typography>

            <Box
              sx={{
                mt: 1,
                p: 2,
                borderRadius: 2,

                maxHeight: 300,
                overflow: "auto",
                fontSize: 14,
              }}
              dangerouslySetInnerHTML={{ __html: aiText }}
            />
          </Box>
        )}
      </CardContent>

      {/* ───── FOOTER ACTIONS ───── */}
      <Box
        sx={{
          p: 2,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,

          display: "flex",
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<AutoFixHighIcon />}
          onClick={handleGenerate}
          disabled={loading}
          fullWidth
        >
          {loading ? "Generating…" : "Generate"}
        </Button>

        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={() =>
            onReplace({
              ...page,
              body: aiText,
            })
          }
          disabled={!aiText}
          fullWidth
          sx={{
            backgroundColor: "#1976d2",
            "& .MuiButton-label": {
              color: "#fff",
            },
            color: "#fff !important",
            "& .MuiButton-startIcon": {
              color: "#fff",
            },
            "&:hover": {
              backgroundColor: "#1565c0",
            },
          }}
        >
          Update Page
        </Button>
      </Box>
    </Card>
  );
}
